class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.cachedBoostedProducts = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this._productsWheelBoundTo = null;
    this._productsWheelHandler = null;
    this._skippHeaderLayoutBound = false;

    this.setupEventListeners();
    this.setupSkippHeaderDropdownLayoutListeners();
  }

  setupSkippHeaderDropdownLayoutListeners() {
    if (this._skippHeaderLayoutBound) return;
    this._skippHeaderLayoutBound = true;
    const onLayout = () => {
      if (this.hasAttribute('open')) this.adjustSkippHeaderDropdownPosition();
    };
    window.addEventListener('resize', onLayout);
    window.addEventListener(
      'scroll',
      () => {
        if (this.hasAttribute('open')) this.adjustSkippHeaderDropdownPosition();
      },
      { passive: true }
    );
  }

  /**
   * mWeb: header results use position:fixed (see skipp-predictive-search.css). Anchor top to the field.
   */
  adjustSkippHeaderDropdownPosition() {
    const results = this.predictiveSearchResults;
    if (!results || !results.classList.contains('skipp-header-search__results')) return;

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (!isMobile) {
      results.style.top = '';
      results.style.removeProperty('max-height');
      return;
    }

    const anchor = this.querySelector('.skipp-header-search__field') || this.input;
    if (!anchor) return;

    const topPx = Math.max(0, Math.round(anchor.getBoundingClientRect().bottom + 6));
    results.style.top = `${topPx}px`;

    const avail = Math.max(160, window.innerHeight - topPx - 16);
    const mh = Math.min(400, avail);
    results.style.setProperty('max-height', `${mh}px`, 'important');
  }

  normalizeTerm(term) {
    return (term || '').toLowerCase().trim();
  }

  getSkippParsed(term) {
    if (this.__skippParsed && this.normalizeTerm(this.__skippParsed.original) === this.normalizeTerm(term)) {
      return this.__skippParsed;
    }
    if (window.SkippSearch && typeof window.SkippSearch.process === 'function') {
      this.__skippParsed = window.SkippSearch.process(term);
      return this.__skippParsed;
    }
    return {
      original: term,
      shopifyQuery: (term || '').trim(),
      fallbackQueries: [],
      intent: {},
      corrections: [],
    };
  }

  skippProductFromLi(li) {
    return {
      id: li?.getAttribute?.('data-product-id') || '',
      title: li?.getAttribute?.('data-title') || '',
      vendor: li?.getAttribute?.('data-vendor') || '',
      type: li?.getAttribute?.('data-type') || '',
      tags: li?.getAttribute?.('data-tags') || '',
      description: li?.getAttribute?.('data-description') || '',
      available: li?.getAttribute?.('data-available') !== 'false',
      boosted: li?.getAttribute?.('data-boosted') === 'true',
      node: li,
    };
  }

  normalizeSearchText(text) {
    // Keep matching case-insensitive and also normalize common synonyms.
    // Example: "trousers" and "pants" should behave like the same query.
    return this.normalizeTerm(text)
      .replace(/\btrousers?\b/g, 'pant')
      .replace(/\bpants?\b/g, 'pant');
  }

  termTokens(term) {
    return this.normalizeSearchText(term).split(/\s+/g).filter(Boolean);
  }

  textMatchesTokens(text, tokens) {
    const t = this.normalizeSearchText(text);
    if (!tokens.length) return true;
    return tokens.every((tok) => {
      if (t.includes(tok)) return true;
      if (tok.length > 3 && tok.endsWith('s')) {
        const singular = tok.slice(0, -1);
        if (singular && t.includes(singular)) return true;
      }
      return false;
    });
  }

  scoreTitleMatch(title, term) {
    const t = this.normalizeSearchText(title);
    const q = this.normalizeSearchText(term);
    if (!q) return 0;
    if (t === q) return 1000;
    if (t.startsWith(q)) return 900;
    if (t.includes(` ${q}`)) return 850;
    if (t.includes(q)) return 800;
    const tokens = q.split(/\s+/g).filter(Boolean);
    if (!tokens.length) return 0;
    const hits = tokens.reduce((acc, tok) => acc + (t.includes(tok) ? 1 : 0), 0);
    // Encourage multi-token matches, but keep it modest.
    return hits * 50;
  }

  scoreFieldMatch(text, term, { exact, startsWith, wordStart, contains, tokenHit }) {
    const t = this.normalizeSearchText(text);
    const q = this.normalizeSearchText(term);
    if (!q) return 0;
    if (!t) return 0;
    if (t === q) return exact;
    if (t.startsWith(q)) return startsWith;
    if (t.includes(` ${q}`)) return wordStart;
    if (t.includes(q)) return contains;
    const tokens = this.termTokens(q);
    if (!tokens.length) return 0;
    const hits = tokens.reduce((acc, tok) => acc + (t.includes(tok) ? 1 : 0), 0);
    return hits * tokenHit;
  }

  isLuxeFromTags(tags) {
    const v = this.normalizeTerm(tags);
    return v.includes('luxe') || v.includes('luxury') || v.includes('skipp-luxe') || v.includes('skipp_luxe');
  }

  normalizeVendorKey(vendor) {
    // Normalize punctuation/ampersands so matching is robust.
    return this.normalizeTerm(vendor)
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  isExclusiveVendor(vendorRaw) {
    const key = this.normalizeVendorKey(vendorRaw);
    return (
      key === 'lords and kings' ||
      key === 'lords & kings' ||
      key === 'street junkies' ||
      key === 'cop underdog'
    );
  }

  shouldMaskVendor(vendorRaw, tagsRaw) {
    if (this.isExclusiveVendor(vendorRaw)) return true;
    if (document.body?.classList?.contains('skipp-luxury-visual')) return true;
    if (document.body?.classList?.contains('luxury-mode')) return true;
    if (document.querySelector?.('.modern-header--lux-only')) return true;
    const tags = String(tagsRaw || '').toLowerCase();
    return tags.split(/[|,]/).some((tag) => tag.trim() === 'luxury');
  }

  getProductMetaFromLi(li) {
    return {
      title: li?.getAttribute?.('data-title') || li?.textContent || '',
      type: li?.getAttribute?.('data-type') || '',
      description: li?.getAttribute?.('data-description') || '',
      tags: li?.getAttribute?.('data-tags') || '',
      vendor: li?.getAttribute?.('data-vendor') || '',
    };
  }

  isHeaderInlineSearch() {
    return !!this.predictiveSearchResults?.classList?.contains('skipp-header-search__results');
  }

  /**
   * Empty query: show trending-only panel (same links as Liquid) without calling the predictive API.
   */
  renderTrendingBootstrap() {
    if (!this.isHeaderInlineSearch() || !this.predictiveSearchResults) return false;

    const script = document.getElementById('skipp-predictive-trending-bootstrap');
    if (!script) return false;

    let items;
    try {
      items = JSON.parse(script.textContent.trim());
    } catch (e) {
      return false;
    }
    if (!Array.isArray(items) || !items.length) return false;

    const searchUrl = typeof routes !== 'undefined' && routes.search_url ? routes.search_url : '/search';
    const esc = (s) =>
      String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const links = items
      .map((item) => {
        const label = item.label || item.q || '';
        const q = item.q || label;
        const href = `${searchUrl}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last`;
        return `<a href="${esc(href)}" class="skipp-predictive-trending__link link link--text" tabindex="-1">${esc(label)}</a>`;
      })
      .join('');

    const html = `
<div id="predictive-search-results" class="skipp-predictive-dropdown-root skipp-predictive-dropdown-root--bootstrap" role="listbox">
  <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--collections-and-products skipp-predictive-groups">
    <div class="skipp-predictive-section skipp-predictive-section--trending" role="group" aria-label="Trending searches">
      <p class="skipp-predictive-section__label" id="skipp-predictive-trending-heading">Trending searches</p>
      <div class="skipp-predictive-trending-list" aria-labelledby="skipp-predictive-trending-heading">${links}</div>
    </div>
  </div>
  <span class="hidden" data-predictive-search-live-region-count-value>Trending searches</span>
</div>`;

    this.predictiveSearchResults.innerHTML = html;
    this.searchTerm = '';
    this.setAttribute('results', 'true');
    this.removeAttribute('loading');
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.setLiveRegionText('Trending searches');
    this.open();
    return true;
  }

  stripQueryHighlights(root) {
    if (!root) return;
    root.querySelectorAll('mark.skipp-predictive-highlight').forEach((m) => {
      const p = m.parentNode;
      if (!p) return;
      p.replaceChild(document.createTextNode(m.textContent), m);
      p.normalize();
    });
  }

  highlightTextInElement(el, rawQuery) {
    if (!el || !rawQuery) return;
    const q = String(rawQuery).trim();
    if (!q) return;
    const needle = q.toLowerCase();
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    let node;
    while ((node = walker.nextNode())) {
      if (!node.textContent || !node.parentElement) continue;
      if (node.parentElement.closest('mark.skipp-predictive-highlight')) continue;
      const txt = node.textContent;
      const idx = txt.toLowerCase().indexOf(needle);
      if (idx === -1) continue;
      const before = txt.slice(0, idx);
      const mid = txt.slice(idx, idx + q.length);
      const after = txt.slice(idx + q.length);
      const frag = document.createDocumentFragment();
      if (before) frag.appendChild(document.createTextNode(before));
      const mark = document.createElement('mark');
      mark.className = 'skipp-predictive-highlight';
      mark.textContent = mid;
      frag.appendChild(mark);
      if (after) frag.appendChild(document.createTextNode(after));
      node.parentNode.replaceChild(frag, node);
      break;
    }
  }

  applyQueryHighlights(term) {
    const root = this.predictiveSearchResults;
    if (!root) return;
    this.stripQueryHighlights(root);
    const q = (term || '').trim();
    if (!q) return;
    const selectors = [
      '.skipp-predictive-product-title',
      '.skipp-predictive-collection-link',
      '.skipp-predictive-trending__link',
      '.skipp-predictive-product-vendor',
    ];
    selectors.forEach((sel) => {
      root.querySelectorAll(sel).forEach((el) => this.highlightTextInElement(el, q));
    });
  }

  productMatchesTerm(li, term) {
    const q = this.normalizeSearchText(term);
    if (!q) return true;

    const meta = this.getProductMetaFromLi(li);
    const title = this.normalizeSearchText(meta.title);
    if (title.includes(q)) return true; // title always wins

    const tokens = this.termTokens(q);
    const combined = `${meta.title} ${meta.type} ${meta.description} ${meta.vendor}`;
    return this.textMatchesTokens(combined, tokens);
  }

  scoreProduct(li, term) {
    const meta = this.getProductMetaFromLi(li);
    const titleScore = this.scoreTitleMatch(meta.title, term);
    const typeScore = this.scoreFieldMatch(meta.type, term, {
      exact: 420,
      startsWith: 330,
      wordStart: 280,
      contains: 220,
      tokenHit: 40,
    });
    const vendorScore = this.scoreFieldMatch(meta.vendor, term, {
      exact: 480,
      startsWith: 380,
      wordStart: 320,
      contains: 260,
      tokenHit: 45,
    });
    const descScore = this.scoreFieldMatch(meta.description, term, {
      exact: 140,
      startsWith: 110,
      wordStart: 90,
      contains: 80,
      tokenHit: 10,
    });

    return titleScore + typeScore + vendorScore + descScore;
  }

  applyClientFiltering() {
    const products = this.querySelectorAll('#predictive-search-results-products-list [data-title]');
    products.forEach((li) => {
      const meta = this.getProductMetaFromLi(li);
      li.style.display = this.isLuxeFromTags(meta.tags) ? 'none' : '';
    });
  }

  maskExclusiveVendorsInResults() {
    // Ensure vendor masking is applied even after the best-seller HTML replacement.
    const productLis = this.querySelectorAll('#predictive-search-results-products-list > li[data-vendor]');
    productLis.forEach((li) => {
      const vendorRaw = li.getAttribute('data-vendor') || '';
      const tagsRaw = li.getAttribute('data-tags') || '';
      if (!this.shouldMaskVendor(vendorRaw, tagsRaw)) return;
      const vendorEl = li.querySelector('.predictive-search__item-vendor');
      if (vendorEl) vendorEl.textContent = 'Exclusive brand';
    });
  }

  setupProductsWheelScroll() {
    const list = this.querySelector('#predictive-search-results-products-list');
    if (!list) return;

    // Rebind if list node changed (innerHTML replaces it).
    if (this._productsWheelBoundTo && this._productsWheelBoundTo !== list && this._productsWheelHandler) {
      this._productsWheelBoundTo.removeEventListener('wheel', this._productsWheelHandler);
      this._productsWheelBoundTo = null;
      this._productsWheelHandler = null;
    }

    if (this._productsWheelBoundTo === list && this._productsWheelHandler) return;

    const handler = (e) => {
      if (list.scrollWidth <= list.clientWidth) return;

      // Only hijack "vertical" wheel to scroll horizontally.
      const dy = e.deltaY || 0;
      const dx = e.deltaX || 0;
      if (Math.abs(dy) <= Math.abs(dx)) return;

      list.scrollLeft += dy;
      e.preventDefault();
    };

    list.addEventListener('wheel', handler, { passive: false });
    this._productsWheelBoundTo = list;
    this._productsWheelHandler = handler;
  }

  getProductDedupeKey(li) {
    const id = li?.getAttribute?.('data-product-id');
    if (id) return `id:${id}`;
    return `title:${this.normalizeSearchText(this.getProductMetaFromLi(li).title)}`;
  }

  productHasTitleMatch(li, term) {
    return this.scoreTitleMatch(this.getProductMetaFromLi(li).title, term) > 0;
  }

  /**
   * Full storefront search — preserves Search & Discovery product boost order,
   * then re-ranks with relevance guardrails so boosts cannot override intent.
   */
  async fetchBoostedProductNodes(term, signal) {
    const parsed = this.getSkippParsed(term);
    const query = (parsed.shopifyQuery || term || '').trim();
    if (query.length < 2) return [];

    const searchBase = typeof routes !== 'undefined' && routes.search_url ? routes.search_url : '/search';
    const params = new URLSearchParams({
      q: query,
      type: 'product',
      view: 'best-sugesstion',
    });
    params.set('options[prefix]', 'last');

    const response = await fetch(`${searchBase}?${params.toString()}`, { signal });
    if (!response.ok) return [];

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return Array.from(doc.querySelectorAll('[data-best-products] > li[data-title]'));
  }

  /**
   * Search & Discovery boosts first (native search.results order), then title matches.
   * Boosts only apply when the product is relevant to the query.
   */
  mergeBoostedSearchResults(term, boostedLis = []) {
    const list = this.querySelector('#predictive-search-results-products-list');
    if (!list) return false;

    const query = (term || '').trim();
    if (query.length < 2) return false;

    const predictiveLis = Array.from(list.querySelectorAll('li[data-title]'));
    const parsed = this.getSkippParsed(term);

    if (window.SkippSearch && typeof window.SkippSearch.rankProducts === 'function') {
      const seen = new Set();
      const combined = [];
      const tryPush = (li, boosted, boostIndex, shopifyIndex) => {
        if (!li) return;
        const product = this.skippProductFromLi(li);
        if (this.isLuxeFromTags(product.tags)) return;
        const key = product.id ? `id:${product.id}` : `title:${this.normalizeSearchText(product.title)}`;
        if (!key || seen.has(key)) return;
        seen.add(key);
        product.boosted = !!boosted;
        product.boostIndex = boostIndex || 0;
        product.shopifyIndex = shopifyIndex || 0;
        product.node = li.cloneNode ? li.cloneNode(true) : li;
        combined.push(product);
      };

      (boostedLis || []).forEach((li, index) => tryPush(li, true, index, index));
      predictiveLis.forEach((li, index) => tryPush(li, false, 0, index));
      if (!combined.length) return false;

      const ranked = window.SkippSearch.rankProducts(combined, parsed, {
        max: (window.SkippSearch.config && window.SkippSearch.config.PREDICTIVE_MAX_PRODUCTS) || 8,
      });
      if (!ranked.length) return false;

      list.innerHTML = '';
      ranked.forEach((product, index) => {
        const li = product.node;
        li.id = `predictive-search-option-product-${index + 1}`;
        li.style.display = '';
        list.appendChild(li);
      });
      return true;
    }

    const seen = new Set();
    const merged = [];
    const maxProducts = 8;

    const tryAdd = (li, { titleMatchOnly = false } = {}) => {
      if (merged.length >= maxProducts || !li) return;
      const meta = this.getProductMetaFromLi(li);
      if (this.isLuxeFromTags(meta.tags)) return;
      if (titleMatchOnly && !this.productHasTitleMatch(li, query)) return;

      const key = this.getProductDedupeKey(li);
      if (!key || seen.has(key)) return;
      seen.add(key);
      merged.push(li);
    };

    boostedLis.forEach((li) => tryAdd(li.cloneNode(true)));
    predictiveLis.forEach((li) => tryAdd(li, { titleMatchOnly: true }));

    if (!merged.length) return false;

    list.innerHTML = '';
    merged.forEach((li, index) => {
      li.id = `predictive-search-option-product-${index + 1}`;
      li.style.display = '';
      list.appendChild(li);
    });
    return true;
  }

  async postProcessResults(boostedLis = null) {
    this.applyClientFiltering();

    let boostedNodes = boostedLis;
    if (boostedNodes == null) {
      const requested = this.normalizeTerm(this.searchTerm);
      try {
        boostedNodes = await this.fetchBoostedProductNodes(this.searchTerm, this.abortController.signal);
        if (this.normalizeTerm(this.searchTerm) !== requested) return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
        boostedNodes = [];
      }
    }

    this.mergeBoostedSearchResults(this.searchTerm, boostedNodes || []);
    this.maskExclusiveVendorsInResults();
    this.setupProductsWheelScroll();
    this.applyQueryHighlights(this.searchTerm);
    this.preserveTypedQueryInResults();
  }

  /**
   * Suggestions may be fetched with a corrected query, but the typed term
   * must stay as-is in the input, view-all link, and empty-state copy.
   */
  preserveTypedQueryInResults() {
    const typed = this.searchTerm;
    if (!typed) return;

    const searchForText = this.querySelector('[data-predictive-search-search-for-text]');
    if (searchForText) searchForText.textContent = typed;

    const viewAll = this.querySelector('.skipp-predictive-view-all__link');
    if (viewAll) {
      try {
        const href = viewAll.getAttribute('href') || '';
        const url = new URL(href, window.location.origin);
        url.searchParams.set('q', typed);
        viewAll.setAttribute('href', `${url.pathname}?${url.searchParams.toString()}`);
      } catch (e) {}
    }

    const parsed = this.getSkippParsed(typed);
    const rewritten = (parsed.shopifyQuery || '').trim();
    if (!rewritten || this.normalizeTerm(rewritten) === this.normalizeTerm(typed)) return;

    this.querySelectorAll(
      '.predictive-search__empty-state-text, [data-predictive-search-live-region-count-value]'
    ).forEach((el) => {
      if (!el.textContent || !el.textContent.includes(rewritten)) return;
      el.textContent = el.textContent.split(rewritten).join(typed);
    });
  }

  async loadPredictiveRecommendations() {
    const container = this.querySelector('[data-skipp-predictive-recommendations]');
    if (!container) return;

    const requested = this.normalizeTerm(this.searchTerm);
    if (requested.length < 2) {
      container.hidden = true;
      container.innerHTML = '';
      return;
    }

    const firstProduct = this.querySelector(
      '#predictive-search-results-products-list > li[data-product-id]'
    );
    const visibleProduct = Array.from(
      this.querySelectorAll('#predictive-search-results-products-list > li[data-product-id]')
    ).find((li) => li.style.display !== 'none');

    const productId = (visibleProduct || firstProduct)?.getAttribute('data-product-id');
    if (!productId) {
      container.hidden = true;
      container.innerHTML = '';
      return;
    }

    const recUrl =
      typeof routes !== 'undefined' && routes.product_recommendations_url
        ? routes.product_recommendations_url
        : '/recommendations/products';

    try {
      const params = new URLSearchParams({
        section_id: 'skipp-search-recommendations',
        product_id: productId,
        limit: '6',
        intent: 'related',
      });
      const res = await fetch(`${recUrl}?${params.toString()}`);
      if (!res.ok) return;
      if (this.normalizeTerm(this.searchTerm) !== requested) return;

      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const inner = doc.querySelector('.skipp-search-recommendations__inner');
      if (!inner) {
        container.hidden = true;
        container.innerHTML = '';
        return;
      }

      container.innerHTML = inner.outerHTML;
      container.hidden = false;
      this.adjustSkippHeaderDropdownPosition();
    } catch (e) {
      container.hidden = true;
    }
  }

  setupEventListeners() {
    if (!this.input) return;
    if (this.input.form) {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      if (this.renderTrendingBootstrap()) return;
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    const q = this.getQuery();
    if (!q.length) {
      if (event.code === 'Escape') this.close(true);
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    if (!previousTerm || !newTerm || previousTerm === newTerm) return;
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    if (!searchForTextElement) return;
    const currentButtonText = searchForTextElement.textContent || '';
    if (!currentButtonText.includes(previousTerm)) return;
    if (currentButtonText.split(previousTerm).length !== 2) return;
    searchForTextElement.textContent = currentButtonText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], this.cachedBoostedProducts[queryKey] || []);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    const requested = this.normalizeTerm(searchTerm);
    const parsed = this.getSkippParsed(searchTerm);
    const shopifyTerm = parsed.shopifyQuery || searchTerm;
    const similarTerm = (parsed.similarQuery || '').trim();
    const fallbackTerm =
      (similarTerm && this.normalizeTerm(similarTerm) !== this.normalizeTerm(shopifyTerm) && similarTerm) ||
      (parsed.fallbackQueries && parsed.fallbackQueries[0]) ||
      '';

    const requestPair = (term) => {
      const params = new URLSearchParams({
        q: term,
        section_id: 'predictive-search',
      });
      params.set('resources[type]', 'product,collection');
      params.set('resources[limit]', '10');
      params.set('resources[limit_scope]', 'each');
      params.set('resources[options][unavailable_products]', 'hide');
      params.set('resources[options][fields]', 'title,vendor,product_type,variants.title,tag');

      const predictiveFetch = fetch(`${routes.predictive_search_url}?${params.toString()}`, { signal });
      const boostedFetch = this.fetchBoostedProductNodes(term, signal);
      return Promise.all([predictiveFetch, boostedFetch]);
    };

    const markupIsEmpty = (resultsMarkup) =>
      !resultsMarkup || /predictive-search__empty-state/.test(resultsMarkup);

    requestPair(shopifyTerm)
      .then(async ([predictiveResponse, boostedNodes]) => {
        if (this.normalizeTerm(this.searchTerm) !== requested) return;

        if (!predictiveResponse.ok) {
          const error = new Error(predictiveResponse.status);
          this.close();
          throw error;
        }

        let text = await predictiveResponse.text();
        let section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        let resultsMarkup = section?.innerHTML;
        let boosted = Array.isArray(boostedNodes) ? boostedNodes : [];

        if (markupIsEmpty(resultsMarkup) && fallbackTerm && fallbackTerm !== shopifyTerm) {
          const [fallbackResponse, fallbackBoosted] = await requestPair(fallbackTerm);
          if (this.normalizeTerm(this.searchTerm) !== requested) return;
          if (fallbackResponse.ok) {
            const fallbackText = await fallbackResponse.text();
            const fallbackSection = new DOMParser()
              .parseFromString(fallbackText, 'text/html')
              .querySelector('#shopify-section-predictive-search');
            const fallbackMarkup = fallbackSection?.innerHTML;
            if (!markupIsEmpty(fallbackMarkup)) {
              resultsMarkup = fallbackMarkup;
              boosted = Array.isArray(fallbackBoosted) ? fallbackBoosted : [];
            }
          }
        }

        if (!resultsMarkup) {
          this.close();
          return;
        }

        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          predictiveSearchInstance.cachedBoostedProducts[queryKey] = boosted;
        });
        this.renderSearchResults(resultsMarkup, boosted);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) return;
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  async renderSearchResults(resultsMarkup, boostedLis = null) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();

    await this.postProcessResults(boostedLis);
    this.loadPredictiveRecommendations();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveCount = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(liveCount ? liveCount.textContent : '');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.predictiveSearchResults || !this.input) return;
    // Inline header dropdown: CSS controls max-height (400px). Skip inline override.
    if (this.predictiveSearchResults.classList.contains('skipp-header-search__results')) {
      this.predictiveSearchResults.style.removeProperty('max-height');
      this.adjustSkippHeaderDropdownPosition();
    } else {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm && this.input) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    if (this.input) {
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', false);
    }
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.resultsMaxHeight = false;
    if (this.predictiveSearchResults) this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
